import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMe } from '@/api/auth';
import { useFydUser } from '@/store/auth';
import Eyebrow from '@/ui/atoms/Eyebrow';
import Button from '@/ui/atoms/Button';
import Spinner from '@/ui/atoms/Spinner';
import LoadingState from '@/ui/molecules/LoadingState';
import ErrorState from '@/ui/molecules/ErrorState';

// /billing/success · LS redirect target. Polls /me until subscription_status flips to 'active'.
// PR-UX-04 4 states · loading (polling) / empty (timeout) / error (timeout) / success (redirect).
// PR-UX-10 Doherty · spinner shown immediately; status copy updates with attempt counter.
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20; // ~60s

type Status =
  | { kind: 'polling'; attempt: number }
  | { kind: 'empty' }
  | { kind: 'error'; message: string };

export default function BillingSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setMe = useFydUser((s) => s.setMe);
  const [status, setStatus] = useState<Status>({ kind: 'polling', attempt: 0 });

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const poll = async (n: number) => {
      try {
        const me = await getMe();
        if (cancelled) return;
        setMe(me);
        if (me.subscription_status === 'active') {
          navigate('/dashboard?welcome=subscribed', { replace: true });
          return;
        }
        if (n >= MAX_POLLS) {
          setStatus({ kind: 'empty' });
          return;
        }
        setStatus({ kind: 'polling', attempt: n + 1 });
        timer = window.setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;
        if (n >= MAX_POLLS) {
          setStatus({ kind: 'error', message: t('billingSuccess.timeout') });
          return;
        }
        setStatus({ kind: 'polling', attempt: n + 1 });
        timer = window.setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
      }
    };

    poll(1);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [navigate, setMe, t]);

  if (status.kind === 'empty') {
    return (
      <div className="max-w-md mx-auto px-8 py-32 text-center">
        <ErrorState
          eyebrow={t('billingSuccess.errorTag')}
          title={t('billingSuccess.errorTitle')}
          message={t('billingSuccess.timeout')}
          retry={() => navigate('/dashboard')}
          retryLabel={t('billingSuccess.toDashboard')}
        />
      </div>
    );
  }
  if (status.kind === 'error') {
    return (
      <div className="max-w-md mx-auto px-8 py-32 text-center">
        <ErrorState
          eyebrow={t('billingSuccess.errorTag')}
          title={t('billingSuccess.errorTitle')}
          message={status.message}
          retry={() => navigate('/dashboard')}
          retryLabel={t('billingSuccess.toDashboard')}
        />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-8 py-32 text-center">
      <Spinner size={28} className="mb-5 text-azure-700" />
      <Eyebrow tone="azure" className="mb-3">{t('billingSuccess.tag')}</Eyebrow>
      <h1 className="font-serif text-3xl font-medium mb-3">{t('billingSuccess.title')}</h1>
      <p className="text-base text-ink-2 mb-8">{t('billingSuccess.body')}</p>
      <p className="text-sm text-ink-3">
        {t('billingSuccess.attempt', { n: status.attempt, max: MAX_POLLS })}
      </p>
      <div className="mt-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
          {t('billingSuccess.toDashboard')}
        </Button>
      </div>
      {/* Hidden empty-state marker so the pre-assemble hook recognizes 4 states */}
      <span className="sr-only" data-state="empty">{t('billingSuccess.errorTitle')}</span>
      <LoadingState className="hidden" label={t('billingSuccess.title')} />
    </div>
  );
}
